import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Translate } from 'react-localize-redux';
import Alert, { ALERT_TYPES } from '../../components/atoms/Alert';
import { IconSizes } from '../../components/foundations/Icon';
import SubPageHeader from '../../components/molecules/SubPageHeader';
import Tooltip from '../../components/molecules/Tooltip';
import { useNavigationBlocker } from '../../components/organisms/NavigationBlocker';
import { Button, Tabs } from '../../components/widgets';
import AppPageStep from '../containers/AppPageStep';
import GenericDimensionPageStep from '../containers/GenericDimensionPageStep';
import MetricsPageStep from '../containers/MetricsPageStep';
import SourcePageStep from '../containers/SourcePageStep';
import ReviewPageStep from '../containers/ReviewPageStep';
import RolePageStep from '../containers/RolePageStep';
import ScreenPageStep from '../containers/ScreenPageStep';
import SectionsPageStep from '../containers/SectionsPageStep';
import UserDetailsPageStep from '../containers/UserDetailsPageStep';
import { SECTION_PAGES_STEPS, SECTIONS_PAGES_CONFIG } from '../sections';
import {
    MANAGE_PERMISSIONS_STEPS,
    RECAPTCHA_PUBLIC_KEY,
    SECTIONS_STEPS,
    translationPrefix as translationRootPrefix,
    PAGE_MODES,
} from '../utils';
import css from './ManagePermissionsPage.css';

const translationPrefix = `${translationRootPrefix}.MANAGE_PERMISSIONS_PAGE`;

const SECTION_PAGES_STEPS_DATA = SECTION_PAGES_STEPS.reduce(
    (acc, step) => ({
        ...acc,
        [step]: SectionsPageStep,
    }),
    {}
);

const KNOWN_STEPS = {
    [MANAGE_PERMISSIONS_STEPS.roleSelection]: RolePageStep,
    [MANAGE_PERMISSIONS_STEPS.userDetails]: UserDetailsPageStep,
    [MANAGE_PERMISSIONS_STEPS.apps]: AppPageStep,
    [MANAGE_PERMISSIONS_STEPS.sources]: SourcePageStep,
    ...SECTION_PAGES_STEPS_DATA,
    [MANAGE_PERMISSIONS_STEPS.metrics]: MetricsPageStep,
    [MANAGE_PERMISSIONS_STEPS.screens]: ScreenPageStep,
    [MANAGE_PERMISSIONS_STEPS.review]: ReviewPageStep,
};

const ManagePermissionsPage = ({
    onHidePage,
    onDirectHidePage,
    hasChanges,
    mode,
    activeMemberType,
    onManagePermissionsFinished,
    onSelectedTabIndexChanged,
    page: { loading, selectedTabIndex, stepValidationStatus },
    pageSteps = [],
    memberName = '',
    dimensionDisplayNames = {},
    roleTypeChanged = false,
    saveMemberError = '',
}) => {
    const translationMemberPrefix = `${translationPrefix}.${activeMemberType}`;
    const recaptchaRef = useRef(null);
    const isEditMode = mode === PAGE_MODES.edit;

    useNavigationBlocker(hasChanges, onDirectHidePage);

    const isGenericDimension = step => !KNOWN_STEPS[step] && dimensionDisplayNames[step];

    const renderStepComponent = stepId => {
        const StepComponent = KNOWN_STEPS[stepId];
        if (StepComponent) {
            let props = {};
            if (SECTIONS_STEPS.includes(stepId)) {
                props = SECTIONS_PAGES_CONFIG.find(page => page.step === stepId);
            }
            return <StepComponent {...props} />;
        }
        return <GenericDimensionPageStep dimension={stepId} />;
    };

    const useProgressiveTabs = !isEditMode || roleTypeChanged;

    const isTabDisabled = index => {
        if (loading) return true;

        if (!useProgressiveTabs) return !stepValidationStatus.isValid;

        if (index > selectedTabIndex + 1) {
            return true;
        }
        if (!stepValidationStatus.isValid && index === selectedTabIndex + 1) {
            return true;
        }
        return false;
    };

    const tabs = pageSteps.map((step, index) => ({
        id: step,
        label: isGenericDimension(step)
            ? `${translationPrefix}.DIMENSION_FILTER.TAB_LABEL`
            : `${translationPrefix}.TABS.${step.toUpperCase()}`,
        labelData: isGenericDimension(step) ? { dimension: dimensionDisplayNames[step] } : undefined,
        disabled: isTabDisabled(index),
    }));

    const safeTabIndex = selectedTabIndex < 0 ? tabs.length - 1 : Math.min(selectedTabIndex, tabs.length - 1);
    const isFirstTab = safeTabIndex === 0;
    const isLastTab = safeTabIndex === tabs.length - 1;

    const onNextClick = async () => {
        if (isLastTab) {
            const token = await recaptchaRef.current.executeAsync();
            onManagePermissionsFinished(token);
        } else {
            onSelectedTabIndexChanged(safeTabIndex + 1);
        }
    };

    const onBackClick = () => {
        if (isFirstTab) {
            onHidePage();
        } else {
            onSelectedTabIndexChanged(safeTabIndex - 1);
        }
    };

    return (
        <div className={css.page}>
            <SubPageHeader
                onGoBack={onHidePage}
                goBackTranslationKey={`${translationMemberPrefix}.BACK`}
                labelTranslationKey={`${translationMemberPrefix}.${mode.toUpperCase()}`}
                className={css.header}
                labelTextReplace={{
                    name: memberName,
                }}
            />
            <div className={css.mainContainer}>
                {saveMemberError && (
                    <Alert
                        type={ALERT_TYPES.ERROR}
                        text={saveMemberError}
                        className={css.alert}
                        alertIconClassName={css.alertIcon}
                    />
                )}
                {tabs.length > 1 && (
                    <Tabs
                        tabs={tabs}
                        selected={tabs[safeTabIndex].id}
                        onClick={tabId => onSelectedTabIndexChanged(tabs.findIndex(tab => tab.id === tabId))}
                        tabClass={css.tab}
                        containerClass={css.tabsContainer}
                        dividerClass={css.tabsDivider}
                        numbered
                        progress
                    />
                )}
                <div className={css.content}>{tabs.length > 0 && renderStepComponent(tabs[safeTabIndex].id)}</div>
                <div className={css.actionsButtonsContainer}>
                    <Button
                        type={isFirstTab ? 'flat' : 'secondary'}
                        onClick={onBackClick}
                        {...(!isFirstTab && {
                            Icon: 'chevron-left',
                            iconLocation: 'left',
                            iconSize: IconSizes.X_SMALL,
                        })}
                        disabled={loading || (!isFirstTab && isEditMode && !stepValidationStatus.isValid)}
                    >
                        <Translate id={`STATIC.BUTTONS.${isFirstTab ? 'CANCEL' : 'BACK'}`} />
                    </Button>
                    <Tooltip title={stepValidationStatus.title} position="top">
                        <Button
                            type="primary"
                            onClick={onNextClick}
                            {...(!isLastTab && {
                                Icon: 'chevron-right',
                                iconLocation: 'right',
                                iconSize: IconSizes.X_SMALL,
                            })}
                            disabled={loading || !stepValidationStatus.isValid}
                            showSpinner={loading}
                            contentClass={classNames({ [css.disabledButton]: !stepValidationStatus.isValid })}
                        >
                            <Translate id={`STATIC.BUTTONS.${isLastTab ? 'SAVE_PERMISSIONS' : 'NEXT'}`} />
                        </Button>
                    </Tooltip>
                </div>
            </div>
            <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_PUBLIC_KEY} />
        </div>
    );
};

ManagePermissionsPage.propTypes = {
    onHidePage: PropTypes.func.isRequired,
    onDirectHidePage: PropTypes.func.isRequired,
    hasChanges: PropTypes.bool.isRequired,
    mode: PropTypes.string.isRequired,
    activeMemberType: PropTypes.string.isRequired,
    onManagePermissionsFinished: PropTypes.func.isRequired,
    onSelectedTabIndexChanged: PropTypes.func.isRequired,
    page: PropTypes.shape({
        show: PropTypes.bool,
        loading: PropTypes.bool,
        selectedTabIndex: PropTypes.number,
        stepValidationStatus: PropTypes.shape({
            isValid: PropTypes.bool,
            title: PropTypes.string,
        }),
    }).isRequired,
    pageSteps: PropTypes.arrayOf(PropTypes.string),
    memberName: PropTypes.string,
    dimensionDisplayNames: PropTypes.objectOf(PropTypes.string),
    roleTypeChanged: PropTypes.bool,
    saveMemberError: PropTypes.string,
};

export default ManagePermissionsPage;
